/**
 * Visor del configurador BUBBLE.
 *
 * A diferencia de `useModel3DViewer` (pensado para un GLB fijo), aquí el modelo
 * cambia con cada parámetro: la escena, la cámara y los controles se crean una
 * vez y solo se sustituye el objeto. Así la cámara no salta al regenerar y se
 * puede seguir mirando las pompas desde donde se estaba.
 *
 * Las mallas llegan con nombre desde el backend: `body` (lo que se imprime),
 * `napkins` y `slot` (solo para ver; no van al STL ni al 3MF).
 */
import { useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import * as THREE from 'three';
import gsap from 'gsap';
import { GLTFLoader } from 'three/examples/jsm/loaders/GLTFLoader.js';
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js';
import { createCamera } from '../Customiz3r/Editor3D/components/camera.js';
import { createRenderer } from '../Customiz3r/Editor3D/components/renderer.js';
import {
    addEnvironment,
    addStudioLights,
    createShadowCatcher,
    createSubtleGrid,
    frameCameraOnObject,
} from '../Customiz3r/Editor3D/components/studio.js';

// Direcciones de cámara (desde el modelo hacia el ojo). El GLB va en Y-up y la
// cara frontal del servilletero mira a -Z (el -Y del backend).
const VIEW_DIRECTIONS = {
    iso: [0.75, 0.55, -1],
    front: [0, 0.12, -1],
    back: [0, 0.12, 1],
    left: [-1, 0.12, 0],
    right: [1, 0.12, 0],
    top: [0, 1, -0.001],
};

function findMesh(object, name) {
    let found = null;
    object?.traverse((child) => {
        if (!found && child.isMesh && (child.name === name || child.parent?.name === name)) found = child;
    });
    return found;
}

function BubbleViewer({ glb, bodyColor, showNapkins, showSlot, view, viewNonce, className }) {
    const containerRef = useRef(null);
    const stateRef = useRef(null);

    // Escena, una sola vez.
    useEffect(() => {
        const container = containerRef.current;
        const scene = new THREE.Scene();
        const camera = createCamera();
        const renderer = createRenderer();
        renderer.setClearColor(0x000000, 0);
        container.appendChild(renderer.domElement);
        addStudioLights(scene, { bottomLight: true });
        // Las luces de estudio vienen de delante; aquí la pieza se mira por
        // todos lados, así que dos contraluces por detrás (una a cada lado)
        // sacan el relieve de las pompas de la pared trasera y sus uniones.
        const backLeft = new THREE.DirectionalLight(0xffffff, 1.4);
        backLeft.position.set(-45, 40, 70);
        const backRight = new THREE.DirectionalLight(0xfff4e8, 1.1);
        backRight.position.set(50, 22, 60);
        const lowFront = new THREE.DirectionalLight(0xffffff, 0.5);
        lowFront.position.set(-20, 8, -60);
        scene.add(backLeft, backRight, lowFront);
        const envTexture = addEnvironment(scene, renderer);
        const grid = createSubtleGrid(100, 100);
        scene.add(grid, createShadowCatcher());

        const controls = new OrbitControls(camera, renderer.domElement);
        controls.enableDamping = true;
        controls.dampingFactor = 0.06;

        const resize = () => {
            const { clientWidth: width, clientHeight: height } = container;
            if (!width || !height) return;
            renderer.setSize(width, height);
            camera.aspect = width / height;
            camera.updateProjectionMatrix();
        };
        resize();
        const observer = new ResizeObserver(resize);
        observer.observe(container);

        let frame;
        const animate = () => {
            frame = requestAnimationFrame(animate);
            controls.update();
            renderer.render(scene, camera);
        };
        animate();

        stateRef.current = { scene, camera, controls, renderer, object: null, framed: false };
        return () => {
            cancelAnimationFrame(frame);
            observer.disconnect();
            controls.dispose();
            scene.traverse((child) => {
                child.geometry?.dispose();
                if (child.material) [].concat(child.material).forEach((material) => material.dispose());
            });
            envTexture.dispose();
            renderer.dispose();
            container.removeChild(renderer.domElement);
            stateRef.current = null;
        };
    }, []);

    // Cada GLB nuevo sustituye al anterior.
    useEffect(() => {
        const state = stateRef.current;
        if (!state || !glb) return undefined;
        let cancelled = false;
        new GLTFLoader().parse(glb, '', (gltf) => {
            if (cancelled || !stateRef.current) return;
            const object = gltf.scene;
            // Sin `applyPrintedMaterial`: vuelve a fundir vértices y recalcula
            // normales, y eso promedia a través de los cantos vivos del corte
            // del hueco y de la base, que se ven como dientes de sierra. El GLB
            // ya trae normales con los cantos duros separados (runner).
            object.traverse((child) => {
                if (!child.isMesh) return;
                child.material = child.material.clone();
                child.material.roughness = 0.5;
                child.material.metalness = 0.0;
                child.material.envMapIntensity = 0.7;
                child.castShadow = true;
                child.receiveShadow = true;
            });
            for (const [name, color, opacity] of [['napkins', '#f5f3ee', 0.55], ['slot', '#4fa3d9', 0.18]]) {
                const mesh = findMesh(object, name);
                if (!mesh) continue;
                mesh.material = new THREE.MeshStandardMaterial({
                    color, transparent: true, opacity, roughness: 0.9, depthWrite: false,
                });
                mesh.castShadow = false;
                mesh.renderOrder = 2;
            }
            // Centrado en X/Z y apoyado en el suelo, como en los otros visores,
            // pero sin mover la cámara salvo la primera vez.
            const box = new THREE.Box3().setFromObject(findMesh(object, 'body') || object);
            const center = box.getCenter(new THREE.Vector3());
            object.position.set(-center.x, -box.min.y, -center.z);

            if (state.object) {
                state.scene.remove(state.object);
                state.object.traverse((child) => {
                    child.geometry?.dispose();
                    if (child.material) [].concat(child.material).forEach((material) => material.dispose());
                });
            }
            state.scene.add(object);
            state.object = object;
            state.applyLook?.();
            if (!state.framed) {
                state.framed = true;
                frameCameraOnObject(state.camera, state.controls, object, { duration: 1.2, zoom: 2.2 });
            }
        }, (error) => console.error('No se pudo leer el GLB del servilletero:', error));
        return () => { cancelled = true; };
    }, [glb]);

    // Color y capas visibles, sin recargar nada.
    useEffect(() => {
        const state = stateRef.current;
        if (!state) return;
        state.applyLook = () => {
            const body = findMesh(state.object, 'body');
            if (body?.material?.color) body.material.color.set(bodyColor);
            const napkins = findMesh(state.object, 'napkins');
            if (napkins) napkins.visible = showNapkins;
            const slot = findMesh(state.object, 'slot');
            if (slot) slot.visible = showSlot;
        };
        state.applyLook();
    }, [bodyColor, showNapkins, showSlot]);

    // Vistas predefinidas. `viewNonce` permite repetir la misma vista.
    useEffect(() => {
        const state = stateRef.current;
        if (!state?.object || !view) return;
        const box = new THREE.Box3().setFromObject(findMesh(state.object, 'body') || state.object);
        const center = box.getCenter(new THREE.Vector3());
        const radius = box.getSize(new THREE.Vector3()).length() / 2;
        const distance = radius / Math.sin((state.camera.fov * Math.PI) / 360) * 1.15;
        const direction = new THREE.Vector3(...(VIEW_DIRECTIONS[view] || VIEW_DIRECTIONS.iso)).normalize();
        const eye = center.clone().addScaledVector(direction, distance);
        gsap.to(state.camera.position, { duration: 0.8, ease: 'power2.inOut', x: eye.x, y: eye.y, z: eye.z });
        gsap.to(state.controls.target, {
            duration: 0.8, ease: 'power2.inOut', x: center.x, y: center.y, z: center.z,
            onUpdate: () => state.controls.update(),
        });
    }, [view, viewNonce]);

    return <div ref={containerRef} className={className} />;
}

BubbleViewer.propTypes = {
    glb: PropTypes.instanceOf(ArrayBuffer),
    bodyColor: PropTypes.string.isRequired,
    showNapkins: PropTypes.bool,
    showSlot: PropTypes.bool,
    view: PropTypes.string,
    viewNonce: PropTypes.number,
    className: PropTypes.string,
};

export default BubbleViewer;
